// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import displayProductsWithoutRealtime from "@pacific/App/Handlers/ProductList/DisplayProductsWithoutRealtime";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import { getSettingsCollection } from "@pacific/App/Store/Selectors/SettingsSelectors";
import Pagination from "@pacific/MobiusOverrides/Pagination/Pagination";
import React, { useCallback, useEffect, useRef } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const {
        pages: {
            productList: { parameter },
        },
    } = state;
    const categoryTreeKey = getDataViewKey(parameter);
    const location = getLocation(state);
    const selectedCategoryPath = getSelectedCategoryPath(state);
    const pathnameLowerCase = location.pathname.toLowerCase();
    const path =
        selectedCategoryPath ||
        (pathnameLowerCase.startsWith("/content/") || pathnameLowerCase.startsWith("/.spire/")
            ? ""
            : location.pathname);

    return {
        categoryTree: state.xData[categoryTreeKey]?.categoryTree || [],
        pagination: state.xData[categoryTreeKey]?.pagination || {},
        path,
        search: location.search,
        searchPath: getSettingsCollection(state).searchSettings.searchPath,
    };
};

const mapDispatchToProps = {
    displayProductsWithoutRealtime,
};

type Props = ReturnType<typeof mapStateToProps> & WidgetProps & ResolveThunks<typeof mapDispatchToProps>;

const RecentlyPurchasedPagination = ({
    categoryTree,
    pagination,
    path,
    search,
    searchPath,
    displayProductsWithoutRealtime,
}: Props) => {
    const prevSearchWithinQueries = useRef<string | undefined>();

    const isSearchPath = useCallback(
        () => !!path.toLowerCase().startsWith(`/${searchPath.toLowerCase()}`),
        [path, searchPath],
    );

    const loadProducts = (page: number, pageSize: number) => {
        displayProductsWithoutRealtime({
            page,
            pageSize,
            path,
            queryString: search,
            isSearch: isSearchPath(),
            includeProductsInSubCategories: true,
            restrictAttributesToCategory: false,
        } as any);
    };

    const changePage = (newPageIndex: number) => {
        loadProducts(newPageIndex, pagination?.pageSize);
    };

    const changeResultsPerPage = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newPageSize = parseInt(event.currentTarget.value, 10);
        loadProducts(pagination?.page, newPageSize);
    };

    useEffect(() => {
        const newSearch = parseQueryString<{ searchWithinQueries?: string }>(search);

        const prevSearchQuery = prevSearchWithinQueries.current;
        const currSearchQuery = newSearch?.searchWithinQueries;

        if ((prevSearchQuery && !currSearchQuery) || (!prevSearchQuery && currSearchQuery)) {
            loadProducts(1, pagination?.pageSize);
        }

        prevSearchWithinQueries.current = currSearchQuery;
    }, [search]);

    if (!categoryTree?.length) {
        return null;
    }

    return (
        <Pagination
            resultsCount={pagination.totalItemCount}
            currentPage={pagination.page}
            resultsPerPage={pagination.pageSize}
            resultsPerPageOptions={pagination.pageSizeOptions}
            onChangePage={changePage}
            onChangeResultsPerPage={changeResultsPerPage}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentlyPurchasedPagination),
    definition: {
        group: "My Account",
        displayName: "Recently Purchased Pagination",
        allowedContexts: ["RecentlyPurchasedProductsPage"] as any,
    },
};

export default widgetModule;
